
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/components/utils/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-xs font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[hsl(var(--button-primary))] text-white hover:bg-[hsl(var(--button-primary-pressed))] active:bg-[hsl(var(--button-primary-pressed))] disabled:bg-[hsl(var(--button-primary-disabled))] disabled:text-white",
        secondary: "bg-[hsl(var(--button-secondary))] text-[hsl(var(--button-primary))] hover:bg-[hsl(var(--button-secondary-pressed))] active:bg-[hsl(var(--button-secondary-pressed))] disabled:bg-[hsl(var(--button-secondary-disabled))] disabled:text-white",
        outline: "border border-[hsl(var(--button-outline-border))] bg-transparent text-[hsl(var(--button-primary))] hover:bg-[hsl(var(--button-outline-border))] active:bg-[hsl(var(--button-outline-border))] disabled:bg-transparent disabled:text-[hsl(var(--button-outline-border))] disabled:border-[hsl(var(--button-outline-border))]",
        ghost: "text-[hsl(var(--button-primary))] hover:bg-[hsl(var(--button-secondary))] active:bg-[hsl(var(--button-secondary-pressed))] disabled:text-[hsl(var(--button-outline-border))]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-full px-4",
        lg: "h-12 rounded-full px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
